import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMinus,
  faPlus,
  faTimes,
  faTractor,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useApiUrl } from "./ApiContext";
import Swal from "sweetalert2";
import { BasketContext } from "./BasketContext";
import completeSeedGuide from "./assets/pdfs/catalogues/CompleteSeedGuide.pdf";

const categoryHierarchy = {
  Forage: [
    "Agricultural Grass",
    "Maize",
    "Kale",
    "Swede",
    "Lucerne",
    "Fodder Beet",
    "Catch Crop Mix",
    "Chicory",
    "Hybrid Brassica",
    "Forage Rape",
    "Stubble Turnip",
    "Arable Silage Mixes",
    "Plantain",
    "Forage Rye",
  ],
  "Energy Crops": ["Energy Beet", "Maize", "Hybrid Rye"],
  "Cereal Crops": ["Rye", "Wheat", "Oats", "Barley", "Triticale"],
  "Oil Crops": ["Sunflower For Oil", "Oilseed Rape"],
  "Pick your own": ["Pumpkin", "Sunflower", "Sweetcorn"],
  Amenity: [
    "Amenity Grass",
    "Landscaping Flowers",
    "Wildflowers",
    "Trees & Hedges",
  ],
  Environmental: {
    Straights: [],
    SFI: [
      "Agricultural Grass",
      "Cover Crops",
      "Mixes",
      "Companion Crops",
      "Trees & Hedges",
    ],
    CSS: ["Agricultural Grass", "Cover Crops", "Mixes", "Trees & Hedges"],
  },
  Gamecover: ["Mixes", "Straights"],
  "Non Seed Sundries": {
    "Silage Clamp": ["Silostop", "Silosolve"],
    "Specialist Fertiliser": [],
  },
};

// Add uid to generic products so keys are unique and stable
const genericProducts = ["Barley", "Rye", "Oats", "Triticale", "Wheat"].map(
  (name) => ({
    uid: `generic:${name}`,
    name,
    description: `Specialist product. Please contact us for more details about ${name}.`,
    imageUrl: `cereal generic.jpg`,
    tags: ["Cereal Crops", name],
    isGeneric: true,
  })
);

const getSubcategories = (parent, child) => {
  if (!parent || !child) return [];
  const children = categoryHierarchy[parent];
  if (typeof children === "object" && !Array.isArray(children)) {
    return children[child] || [];
  }
  return [];
};

const sanitizeInput = (value) => {
  return value.replace(/[^\w\s.-]/gi, "");
};

const Products = () => {
  const normalize = (s) =>
    s
      ? s
          .replace(/\s+/g, " ")
          .replace(/\u00A0/g, " ")
          .replace(/^\s+|\s+$/g, "")
          .toLowerCase()
          .replace(/\.jpeg$/, ".jpg")
          .replace(/\.jpe$/, ".jpg")
      : "";

  const productImages = importAll(
    require.context(
      "./assets/images/products",
      false,
      /\.(png|jpg|jpeg|webp|svg)$/
    )
  );

  function importAll(r) {
    let images = {};
    r.keys().forEach((key) => {
      const filename = key
        .replace("./", "")
        .toLowerCase()
        .replace(/\.jpeg$/, ".jpg")
        .replace(/\.jpg$/, ".jpg");
      images[filename] = r(key);
    });
    return images;
  }

  const navigate = useNavigate();
  const location = useLocation();

  const apiUrl = useApiUrl();
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  const params = new URLSearchParams(location.search);

  const initialParent =
    params.get("parent") || localStorage.getItem("selectedParent") || "";
  const initialChild =
    params.get("child") || localStorage.getItem("selectedChild") || "";
  const initialSub =
    params.get("sub") || localStorage.getItem("selectedSub") || "";
  const initialSearch =
    params.get("search") || localStorage.getItem("searchTerm") || "";

  const [selectedParent, setSelectedParent] = useState(initialParent);
  const [selectedChild, setSelectedChild] = useState(initialChild);
  const [selectedSub, setSelectedSub] = useState(initialSub);
  const [searchTerm, setSearchTerm] = useState(initialSearch);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const { addToBasket } = useContext(BasketContext);
  const [quantities, setQuantities] = useState({});

  const [countdown, setCountdown] = useState(5);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${apiUrl}/products`);
        setProducts(response.data);

        const params = new URLSearchParams(location.search);
        const productName = params.get("name");

        if (productName) {
          const product = response.data.find((p) => p.name === productName);
          setSelectedProduct(product);
        }
        setLoading(false);
      } catch (error) {
        console.error("Error fetching products:", error);
        setLoading(false);
      }
    };
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    localStorage.setItem("selectedParent", selectedParent);
  }, [selectedParent]);

  useEffect(() => {
    localStorage.setItem("selectedChild", selectedChild);
  }, [selectedChild]);

  useEffect(() => {
    localStorage.setItem("selectedSub", selectedSub);
  }, [selectedSub]);

  useEffect(() => {
    localStorage.setItem("searchTerm", sanitizeInput(searchTerm));
  }, [searchTerm]);

  // Quantity state should be keyed by uid to avoid collisions
  useEffect(() => {
    if (selectedProduct) {
      setQuantities({ [selectedProduct.uid]: 1 });
    }
  }, [selectedProduct]);

  useEffect(() => {
    if (selectedProduct) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [selectedProduct]);

  const handleCloseModal = () => setSelectedProduct(null);
  const handleClearSearch = () => setSearchTerm("");
  const handleResetFilters = () => {
    setSelectedParent("");
    setSelectedChild("");
    setSelectedSub("");
    setSearchTerm("");
    localStorage.removeItem("searchTerm");
    localStorage.removeItem("selectedParent");
    localStorage.removeItem("selectedChild");
    localStorage.removeItem("selectedSub");
  };

  const handleProductClick = (product) => {
    // Preserve uid while sanitizing name
    setSelectedProduct({ ...product, name: sanitizeInput(product.name) });
  };

  const handleCompareProduct = (product) => {
    let updatedProducts =
      JSON.parse(localStorage.getItem("selectedProducts")) || [];

    if (updatedProducts.length < 3) {
      if (
        updatedProducts.length === 0 ||
        updatedProducts[updatedProducts.length - 1] !== null
      ) {
        // prevent duplicates by uid
        if (!updatedProducts.some((p) => p && p.uid === product.uid)) {
          updatedProducts.push(product);
        }
      } else {
        const index = updatedProducts.findIndex((p) => p === null);
        updatedProducts[index] = product;
      }
      localStorage.setItem("selectedProducts", JSON.stringify(updatedProducts));
      navigate("/compare");
    } else {
      Swal.fire({
        icon: "warning",
        title: "Comparison Limit Reached",
        text: "You can only compare 3 products at a time. Do you want to go to the comparison tool and change your selections?",
        showCancelButton: true,
        confirmButtonText: "Change Selections",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/compare");
        }
      });
    }
  };

  // Compare by uid, not name
  const isProductInComparison = (product) => {
    return selectedProducts.some((p) => p && p.uid === product.uid);
  };

  const combinedProducts = [...products, ...genericProducts];

  const filteredProducts = combinedProducts.filter((product) => {
    const tags = product.tags || [];
    const name = (product.name || "").toLowerCase();
    const description = (product.description || "").toLowerCase();

    const matchesSearch =
      name.includes(searchTerm.toLowerCase()) ||
      description.includes(searchTerm.toLowerCase());

    let matchesParent = true;
    if (selectedParent) {
      const parentStructure = categoryHierarchy[selectedParent];
      let validTags = [];

      if (Array.isArray(parentStructure)) {
        validTags = [...parentStructure];
      } else if (typeof parentStructure === "object") {
        for (const [child, subTags] of Object.entries(parentStructure)) {
          validTags.push(child);
          validTags.push(...subTags);
        }
      }

      validTags.push(selectedParent);

      matchesParent = tags.some((tag) => validTags.includes(tag));
    }

    let matchesChild = true;
    if (selectedChild) {
      matchesChild = tags.includes(selectedChild);
    }

    let matchesSub = true;
    if (selectedSub) {
      if (
        selectedParent === "Environmental" &&
        (selectedChild === "CSS" || selectedChild === "SFI")
      ) {
        matchesSub = tags.includes(`${selectedChild} ${selectedSub}`);
      } else if (selectedParent === "Gamecover") {
        matchesSub = tags.includes(`Gamecover ${selectedSub}`);
      } else if (selectedParent === "Non Seed Sundries") {
        matchesSub =
          tags.includes(`${selectedParent} ${selectedChild}`) &&
          tags.includes(selectedSub);
      } else {
        matchesSub = tags.includes(selectedSub);
      }
    }

    let strictStraightsCheck = true;
    if (selectedParent === "Gamecover" && selectedChild === "Straights") {
      strictStraightsCheck = tags.includes("Gamecover Straights");
    } else if (
      selectedParent === "Environmental" &&
      selectedChild === "Straights"
    ) {
      strictStraightsCheck = tags.includes("Environmental Straights");
    }

    return (
      matchesSearch &&
      matchesParent &&
      matchesChild &&
      matchesSub &&
      strictStraightsCheck
    );
  });

  const formatFieldLabel = (key) => {
    const labelMap = {
      sowingTime: "Sowing Time",
      lasts: "Lasts",
      grazingPeriod: "Grazing Period",
      crudeProtein: "Crude Protein",
      dm: "Dry Matter (DM)",
      faoEstimate: "FAO Estimate",
      cobRipenessScore: "COB Ripeness Score",
      maturityGroup: "Maturity Group",
      size: "Size",
    };
    return labelMap[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  const getDisplayTags = (
    product,
    selectedParent,
    selectedChild,
    selectedSub,
    categoryHierarchy
  ) => {
    const displayTags = [];

    const lowerTags = product.tags.map((t) => t.toLowerCase());

    // Always show CSS and SFI if present
    if (lowerTags.some((t) => t.includes("css"))) {
      displayTags.push({ tag: "CSS", color: "product-tag-yellow" });
    }

    if (lowerTags.some((t) => t.includes("sfi"))) {
      displayTags.push({ tag: "SFI", color: "product-tag-green" });
    }

    // Show the most specific dropdown-based tag if selected
    if (selectedSub && product.tags.includes(selectedSub)) {
      displayTags.push({ tag: selectedSub, color: "product-tag-blue" });
    } else if (selectedChild && product.tags.includes(selectedChild)) {
      displayTags.push({ tag: selectedChild, color: "product-tag-blue" });
    } else if (selectedParent) {
      const structure = categoryHierarchy[selectedParent];
      let parentTags = [];

      if (Array.isArray(structure)) {
        parentTags = structure;
      } else if (typeof structure === "object") {
        Object.values(structure).forEach((subArray) => {
          parentTags.push(...subArray);
        });
      }

      const match = parentTags.find((tag) => product.tags.includes(tag));
      if (match) {
        displayTags.push({ tag: match, color: "product-tag-blue" });
      } else {
        displayTags.push({ tag: selectedParent, color: "product-tag-blue" });
      }
    }

    // If no dropdowns are selected, auto-detect the deepest matching tag
    if (!selectedParent && !selectedChild && !selectedSub) {
      outerLoop: for (const [parent, structure] of Object.entries(
        categoryHierarchy
      )) {
        if (Array.isArray(structure)) {
          for (const tag of structure) {
            if (product.tags.includes(tag)) {
              displayTags.push({ tag, color: "product-tag-blue" });
              break outerLoop;
            }
          }
        } else if (typeof structure === "object") {
          for (const [child, subTags] of Object.entries(structure)) {
            for (const sub of subTags) {
              if (product.tags.includes(sub)) {
                displayTags.push({ tag: sub, color: "product-tag-blue" });
                break outerLoop;
              }
            }
            if (product.tags.includes(child)) {
              displayTags.push({ tag: child, color: "product-tag-blue" });
              break outerLoop;
            }
          }
        }
      }
    }

    return displayTags;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (
      params.has("parent") ||
      params.has("child") ||
      params.has("sub") ||
      params.has("search")
    ) {
      params.delete("parent");
      params.delete("child");
      params.delete("sub");
      params.delete("search");
      navigate(
        { pathname: location.pathname, search: params.toString() },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [visibleCount, setVisibleCount] = useState(9);
  const [loadingMore, setLoadingMore] = useState(false);
  const observer = useRef();
  const loadingDelay = 700; // ms

  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          visibleCount < filteredProducts.length
        ) {
          setLoadingMore(true);
          setTimeout(() => {
            setVisibleCount((prev) =>
              Math.min(prev + 9, filteredProducts.length)
            );
            setLoadingMore(false);
          }, loadingDelay);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, visibleCount, filteredProducts.length]
  );

  useEffect(() => {
    setVisibleCount(9);
  }, [selectedParent, selectedChild, selectedSub, searchTerm]);

  return (
    <div className="products-container">
      <h1>Product Catalogue</h1>
      <p>
        Here you can find a variety of agricultural products. Use the search bar
        to find specific products or filter by dropdowns.
      </p>
      <p className="pdfguideslinks">
        <a
          href={completeSeedGuide}
          target="_blank"
          rel="noopener noreferrer"
        >
          Complete Seed Guide PDF
        </a>
      </p>

      <div className="search-filter-container">
        <div className="dropdown-filters">
          <select
            value={selectedParent}
            onChange={(e) => {
              setSelectedParent(e.target.value);
              setSelectedChild("");
              setSelectedSub("");
            }}
          >
            <option value="">Select Category</option>
            {Object.keys(categoryHierarchy)
              .sort()
              .map((parent) => (
                <option key={parent} value={parent}>
                  {parent}
                </option>
              ))}
          </select>

          {selectedParent &&
            Array.isArray(categoryHierarchy[selectedParent]) && (
              <select
                value={selectedChild}
                onChange={(e) => {
                  setSelectedChild(e.target.value);
                  setSelectedSub("");
                }}
              >
                <option value="">Select Subcategory</option>
                {categoryHierarchy[selectedParent]
                  .slice()
                  .sort()
                  .map((child) => (
                    <option key={child} value={child}>
                      {child}
                    </option>
                  ))}
              </select>
            )}

          {selectedParent &&
            typeof categoryHierarchy[selectedParent] === "object" &&
            !Array.isArray(categoryHierarchy[selectedParent]) && (
              <select
                value={selectedChild}
                onChange={(e) => {
                  setSelectedChild(e.target.value);
                  setSelectedSub("");
                }}
              >
                <option value="">Select Subcategory</option>
                {Object.keys(categoryHierarchy[selectedParent])
                  .sort()
                  .map((child) => (
                    <option key={child} value={child}>
                      {child}
                    </option>
                  ))}
              </select>
            )}

          {getSubcategories(selectedParent, selectedChild).length > 0 && (
            <select
              value={selectedSub}
              onChange={(e) => setSelectedSub(e.target.value)}
            >
              <option value="">Select Subcategory</option>
              {getSubcategories(selectedParent, selectedChild)
                .slice()
                .sort()
                .map((tag) => (
                  <option key={tag} value={tag}>
                    {tag}
                  </option>
                ))}
            </select>
          )}
        </div>

        <div className="search-bar-container">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(sanitizeInput(e.target.value))}
            className="search-bar"
          />
        {searchTerm && (
            <button onClick={handleClearSearch} className="clear-search-button">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
        {(selectedParent || selectedChild || selectedSub) && (
          <button onClick={handleResetFilters} className="reset-filters-button">
            Reset Filters
          </button>
        )}
      </div>

      <div className="products-grid">
        {filteredProducts.length > 0 ? (
          filteredProducts.slice(0, visibleCount).map((product, index) => {
            const matchedKey = Object.keys(productImages).find(
              (key) => key === normalize(product.imageUrl).toLowerCase()
            );
            const resolvedImage = productImages[matchedKey];

            return (
              <div
                ref={index === visibleCount - 1 ? lastProductRef : null}
                className="product-card fade-in"
                key={product.uid || `${product.name}::${product.imageUrl || 'noimg'}`}
                onClick={() =>
                  !product.isGeneric && handleProductClick(product)
                }
              >
                <img
                  src={resolvedImage}
                  alt={product.name}
                  className="product-image"
                />
                <h2>{product.name}</h2>
                <p
                  dangerouslySetInnerHTML={{
                    __html:
                      product.description.length > 100
                        ? `${product.description
                            .slice(0, 100)
                            .replace(/\\/g, "<br />")}...`
                        : product.description.replace(/\\/g, "<br />"),
                  }}
                ></p>
                {!product.isGeneric && (
                  <button
                    className="view-details-button"
                    onClick={() => handleProductClick(product)}
                  >
                    View Details
                  </button>
                )}
                {product.isGeneric && (
                  <button
                    className="contact-us-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/contact");
                    }}
                  >
                    Contact Us
                  </button>
                )}
                <div className="product-tags">
                  {getDisplayTags(
                    product,
                    selectedParent,
                    selectedChild,
                    selectedSub,
                    categoryHierarchy
                  ).map(({ tag, color }, idx) => (
                    <span key={idx} className={`product-tag ${color}`}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            );
          })
        ) : loading ? (
          <div className="no-products">Loading...</div>
        ) : (
          <div className="no-products">
            No products found, please adjust or reset filters.
          </div>
        )}
      </div>

      {loadingMore && <div className="loading-spinner"></div>}

      {selectedProduct && (
        <div className="modal-backdrop" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button onClick={handleCloseModal} className="close-modal">
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <img
              src={
                productImages[
                  Object.keys(productImages).find(
                    (key) =>
                      normalize(key) ===
                      normalize(selectedProduct.imageUrl.toLowerCase())
                  )
                ]
              }
              alt={selectedProduct.name}
              className="modal-image"
            />
            <h2>{selectedProduct.name}</h2>
            {selectedProduct.description && (
              <p
                className="modal-product-desc"
                dangerouslySetInnerHTML={{
                  __html: selectedProduct.description.replace(/\\/g, "<br />"),
                }}
              ></p>
            )}
            {selectedProduct.additionalDetails && (
              <table className="product-info-table">
                <tbody>
                  {Object.entries(selectedProduct.additionalDetails).map(
                    ([key, value]) =>
                      value && (
                        <tr key={key}>
                          <td>{formatFieldLabel(key)}</td>
                          <td>{value}</td>
                        </tr>
                      )
                  )}
                </tbody>
              </table>
            )}
            <div className="modal-product-info-btns">
              <div className="quantity-controls">
                <button
                  className="quantity-button"
                  onClick={() =>
                    setQuantities((prevQuantities) => ({
                      ...prevQuantities,
                      [selectedProduct.uid]: Math.max(
                        1,
                        (prevQuantities[selectedProduct.uid] || 1) - 1
                      ),
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faMinus} />
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantities[selectedProduct.uid] || 1}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (value > 0) {
                      setQuantities((prevQuantities) => ({
                        ...prevQuantities,
                        [selectedProduct.uid]: value,
                      }));
                    }
                  }}
                  className="quantity-input"
                />
                <button
                  className="quantity-button"
                  onClick={() =>
                    setQuantities((prevQuantities) => ({
                      ...prevQuantities,
                      [selectedProduct.uid]:
                        (prevQuantities[selectedProduct.uid] || 1) + 1,
                    }))
                  }
                >
                  <FontAwesomeIcon icon={faPlus} />
                </button>
              </div>
              <button
                onClick={() => {
                  addToBasket({
                    ...selectedProduct,
                    quantity: quantities[selectedProduct.uid] || 1,
                  });

                  let countdown = 5;

                  const timerInterval = setInterval(() => {
                    countdown -= 1;
                    Swal.update({
                      cancelButtonText: `Keep Browsing (${countdown})`,
                    });

                    if (countdown === 0) {
                      clearInterval(timerInterval);
                      Swal.close();
                    }
                  }, 1000);

                  Swal.fire({
                    icon: "success",
                    title: "Added to Tractor",
                    text: `${selectedProduct.name} has been added to your tractor.`,
                    showCancelButton: true,
                    confirmButtonText: "Go to Tractor",
                    cancelButtonText: `Keep Browsing (${countdown})`,
                    timer: 5000,
                    timerProgressBar: true,
                    willClose: () => {
                      clearInterval(timerInterval);
                    },
                  }).then((result) => {
                    if (result.isConfirmed) {
                      navigate("/tractor");
                    } else if (result.dismiss === Swal.DismissReason.cancel) {
                      handleCloseModal();
                    }
                  });
                }}
                className="add-to-basket-button"
              >
                Add to <FontAwesomeIcon icon={faTractor} />
              </button>
              {!isProductInComparison(selectedProduct) && (
                <button
                  onClick={() => handleCompareProduct(selectedProduct)}
                  className="compare-btn"
                >
                  Compare Product
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
